import React, { useCallback, useState, useEffect, useContext, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import 'trix'
import 'trix/dist/trix.css';
import ComboboxAutocompleteAPI from "./combobox_autocomplete_api";
import { AuthContext } from '../contexts/auth';
import { getEmployeesWithActiveContract } from '../services/api_calendar';

const FormCreaRimborsoCompany = ({ submitForm, afterSubmit }) => {
  const { token } = useContext(AuthContext);
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState('');
  const [title, setTitle] = useState('');
  const [importo, setImporto] = useState('');
  const [date, setDate] = useState('');
  const [dipendenti, setDipendenti] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState({ id: '', name: '' });

  const descriptionRef = useRef();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState('');
  const [titleError, setTitleError] = useState('');
  const [importoError, setImportoError] = useState('');
  const [employeeError, setEmployeeError] = useState('');

  const onDrop = useCallback(acceptedFiles => {
    const validFile = acceptedFiles.find(file => {
      if (file.size > 2 * 1024 * 1024) {
        alert('File troppo grande. Massimo 2mb');
        return false;
      }
      return true;
    });

    if (validFile) {
      setFile(validFile);
    }
  }, []);

  const handleSelectEmployee = (selected) => {
    setSelectedEmployee(selected);
    setEmployeeError('');
  };

  const { getRootProps, getInputProps } = useDropzone({ onDrop, multiple: false });

  const handleImportoChange = (e) => {
    let value = e.target.value;

    if (!isNaN(value)) {
      const decimalIndex = value.indexOf('.');
      if (decimalIndex !== -1 && value.length - decimalIndex > 3) {
        value = value.substring(0, decimalIndex + 3);
      }

      setImporto(value);
    }
  };

  const handleDateChange = (e) => {
    let inputValue = e.target.value;

    const dateParts = inputValue.split('-');

    if (dateParts[0]?.length > 4) {
        dateParts[0] = dateParts[0].slice(0, 4);
        inputValue = dateParts.join('-');
    }

    setDate(inputValue);
  };
  

  const validateForm = () => {
    let valid = true;
  
    setFormError('');
    setTitleError('');
    setImportoError('');
    setEmployeeError('');
  
    if (!selectedEmployee.id) {
      setEmployeeError('Selezionare un dipendente');
      valid = false;
    }
  
    if (!title) {
      setTitleError('Il titolo è richiesto');
      valid = false;
    }
  
    if (!importo) {
      setImportoError('L\'importo è richiesto');
      valid = false;
    } else if (isNaN(importo) || parseFloat(importo) <= 0) {
      setImportoError('Inserisci un importo valido');
      valid = false;
    }
  
    return valid;
  };


  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    const formData = new FormData();
    formData.append('employee_id', selectedEmployee.id);
    formData.append('title', title);
    formData.append('description', descriptionRef.current.value);
    formData.append('amount', importo);
    formData.append('date', date);

    if (file) {
      formData.append('file_binary', file);
    }

    let success
    setLoading(true);

    try {
      success = await submitForm(formData);
    } catch (error) {
      setError(error);
    } finally {
      setLoading(false);
    }

    if (success) {
      setSelectedEmployee('');
      setTitle('');
      setDescription('');
      setFile(null);
      setImporto('');
      setDate('');

      await afterSubmit();
    }

  };

  useEffect(() => {
    const handleFileAccept = (event) => {
      event.preventDefault();
    };
  
    document.addEventListener("trix-file-accept", handleFileAccept);
  
    return () => {
      document.removeEventListener("trix-file-accept", handleFileAccept);
    };
  }, []);

  const css = `
    .trix-button.trix-button--icon.trix-button--icon-attach {
      display: none;
    }
    .trix-button-group.trix-button-group--file-tools {
    display: none;
    }
  `;

  return (
    <div className=" mx-auto p-6 bg-white shadow-md rounded-md">
      <style>
        {css}
      </style>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="title">
            Seleziona Dipendente
          </label>
          <div className="relative w-full border border-gray-500 rounded-md">
            <ComboboxAutocompleteAPI
              getData={async (query) => {
                if (query.trim() === '') {
                  return Promise.resolve([]);
                }

                const page = 1;
                const pageSize = 5;

                try {
                  const res = await getEmployeesWithActiveContract(token, query, 'hr_active', null, page, pageSize);
                  const employeesArray = Array.isArray(res.results) ? res.results : [];
                  return employeesArray;
                } catch (error) {
                  console.error("Errore durante il recupero dei dati:", error);
                  return [];
                }
              }}
              getDisplay={(employee) => 
                employee.last_name && employee.first_name 
                  ? `${employee.last_name} ${employee.first_name}` 
                  : employee.name || employee.cf || ''
              }
              getId={(employee) => employee.id}
              onChange={handleSelectEmployee} 
              currentValue={selectedEmployee || null}
              placeholder="Digita per cercare il dipendente"
            />
          </div>
          {employeeError && <div className="text-red-500 text-sm mt-1">{employeeError}</div>}
        </div>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="title">
            Oggetto
          </label>
          <input
            id="title"
            type="text"
            placeholder="Oggetto"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setTitleError('');
            }}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          />
          {titleError && <div className="text-red-500 text-sm mt-1">{titleError}</div>}
        </div>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="date">
            Data
          </label>
          <input
            id="date"
            type="date"
            placeholder="Data"
            value={date}
            onChange={handleDateChange}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>

        {file ? (
          <div className="mb-4">
            <h2 className="text-lg font-bold mb-2">File che stai caricando:</h2>
            <div className="mb-2">
              <p><strong>Nome:</strong> {file.name}</p>
              <p><strong>Tipo:</strong> {file.type}</p>
              <p><strong>Dimensione:</strong> {(file.size / 1024).toFixed(2)} KB</p>
              <button
                type="button"
                onClick={() => {
                  setFile(null);
                }}
                className="text-red-500 font-semibold">Annulla</button>
            </div>
          </div>
        ) : (
          <div {...getRootProps()} className="mb-4 border-dashed border-2 border-gray-300 p-4 text-center cursor-pointer">
            <input {...getInputProps()} />
            <p>Trascina qui il file o clicca per selezionare un file</p>
          </div>
        )}

        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
            Note aggiuntive
          </label>
          <input
            id="x"
            type="hidden"
            ref={descriptionRef}
          />
          <trix-editor input="x"></trix-editor>
        </div>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="importo" >
            Importo
          </label>
          <input
            id="importo"
            type="number"
            value={importo}
            onChange={handleImportoChange}
            placeholder="Somma da rimborsare"
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            step="0.01"
          />
          {importoError && <div className="text-red-500 text-sm mt-1">{importoError}</div>}
        </div>

        <div className="flex items-center justify-between">

          {loading ? (
            <div className="text-sm font-semibold text-gray-900">Salvataggio in corso...</div>
          ) : (
              <button
                type="submit"
                className="bg-indigo-500 w-full text-white text-sm px-4 py-2 rounded-md hover:bg-indigo-200 focus:outline-none focus:bg-indigo-200 w-24 font-semibold"
              >
                Salva
              </button>
          )}

          
        </div>
      </form>
    </div>
  );
};


export default FormCreaRimborsoCompany;
